import { useRef } from 'react';



/**
 * Creates a debounced version of the given action function.
 * When the debounced function is invoked, it will not call the
 * original function until after the specified delay.
 * If the debounced function is invoked again before the delay
 * has expired, the delay will be reset to the original value.
 * If disableFirstFetch is true, the first call to the debounced
 * function will be ignored, allowing for the component to be
 * initialized with the action function without triggering an
 * immediate API call.
 * @param {function} action
 * @param {object} options
 * @param {number} options.delay
 * @param {boolean} options.disableFirstFetch
 * @returns {function}
 */
export const useDebouncedAction = (
  action,
  options = { delay: 700, disableFirstFetch: false }
) => {
  const timerRef = useRef();
  const isFirstFetch = useRef(true);
  const debouncedAction = (...payload) => {
    if (options.disableFirstFetch && isFirstFetch.current) {
      isFirstFetch.current = false;
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => action(...payload), options.delay);
  };
  return debouncedAction;
};
